<template>
  <div class="bu-card-template" :class="template.class_name">
    <!-- Profile Section -->
    <div class="profile-section">
      <b-avatar
        :src="data.avatar.url"
        :text="initials"
        size="80"
        variant="ct-custom-primary"
      />
      <div class="full-name" v-if="fullName">
        {{ fullName }}
      </div>
      <div class="job-title" v-if="data.job_title">
        {{ data.job_title }}
      </div>
    </div>
    <!-- Logo Services -->
    <div class="logo-services" v-if="data.services && data.services.length">
      <b-img
        v-for="(service, index) in data.services"
        :key="index"
        :src="getProgramImageRoute(service.service_id)"
        :alt="service.name"
        v-b-tooltip="service.name"
        width="40"
        height="40"
        class="rounded-circle cursor-pointer"
        @click="handleOpenLink(service.link)"
      />
    </div>
    <!-- Business Hours -->
    <div class="business-hours" v-if="data.schedules && data.schedules.length">
      <div class="label">Business Hours</div>
      <div class="hours-list">
        <div
          class="hours-item"
          v-for="(schedule, index) in data.schedules"
          :key="index"
        >
          <span class="day">
            {{ schedule.label }}
          </span>
          <div class="time-container">
            <span class="time">
              {{ schedule.hours }}
            </span>
            <b-badge variant="light-primary"> PST </b-badge>
          </div>
        </div>
      </div>
    </div>
    <!-- Save Contact Button -->
    <b-button variant="ct-custom-primary" @click="$emit('saveContact')">
      <b-spinner small type="grow" v-if="isSavingContact" />
      {{ saveContactButtonText }}
    </b-button>
    <!-- Contact Information -->
    <div class="contact-info" v-if="contactInformation.length">
      <div
        class="info-item"
        v-for="(info, index) in contactInformation"
        :key="index"
        @click="handleOpenLink(info.href)"
      >
        <div class="info-icon" :class="info.type">
          <tabler-icon
            :icon="getIcon(info.type)"
            size="20"
            class="custom-icon"
          />
        </div>
        <div class="info">
          <div class="label">
            {{ info.label }}
          </div>
          <div class="value" :class="info.type">
            <span
              v-if="info.value"
              :class="{ 'd-block': info.type == 'address' }"
            >
              {{ info.value }}
            </span>
            <span
              v-if="info.extra"
              :class="{ 'd-block': info.type == 'address' }"
            >
              {{ info.extra }}
            </span>
          </div>
        </div>
        <tabler-icon icon="ChevronRightIcon" size="16" class="custom-icon" />
      </div>
    </div>
  </div>
</template>
<script>
import translations from "@/views/pages/digital-business-card/data/translations.js";

export default {
  props: {
    template: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    isSavingContact: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    t() {
      return translations[this.selectedLanguage] || translations.en;
    },
    isUserCard() {
      return this.data.type === "USER";
    },
    isDepartmentCard() {
      return this.data.type === "DEPARTMENT";
    },
    initials() {
      if (!this.isDepartmentCard && !this.isUserCard) return "";

      if (this.isDepartmentCard) {
        const name = this.data.department_name || "";
        return name
          .split(" ")
          .filter((n) => n.trim().length > 0)
          .slice(0, 2)
          .map((n) => n[0])
          .join("")
          .toUpperCase();
      }

      const first = this.data.first_name?.trim().charAt(0).toUpperCase() || "";
      const last = this.data.last_name?.trim().charAt(0).toUpperCase() || "";

      return (first + last).trim();
    },
    fullName() {
      if (this.isDepartmentCard)
        return `${this.data.department_name || ""}`.trim();

      return `${this.data.first_name || ""} ${
        this.data.last_name || ""
      }`.trim();
    },
    contactInformation() {
      const info = [];

      // Phone
      if (this.data.phone) {
        const extension = this.data.extension
          ? `, Ext. ${this.data.extension.trim()}`
          : "";

        info.push({
          label: this.t.callMe,
          value: this.data.phone + extension,
          href: `tel:+1${this.data.phone}`,
          type: "phone",
        });
      }

      //direct line
      if (this.data.direct_line) {
        info.push({
          label: this.t.directLine,
          value: this.data.direct_line,
          href: `tel:+1${this.data.direct_line}`,
          type: "direct_line",
        });
      }

      //whatsapp
      if (this.data.wsp_number) {
        const wsp = this.data.wsp_number.trim().replace(/\D/g, "");
        const formattedWsp = wsp
          ? wsp.slice(1).replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
          : null;

        if (formattedWsp) {
          info.push({
            label: this.t.whatsapp,
            value: formattedWsp,
            href: `https://wa.me/${wsp}`,
            type: "wsp",
          });
        }
      }

      // Email
      if (this.data.email) {
        info.push({
          label: this.t.emailMe,
          value: this.data.email.trim(),
          href: `mailto:${this.data.email.trim()}`,
          type: "email",
        });
      }

      //mailing address
      if (this.data.mailling_address || this.data.mailing_address_line2) {
        const { mailling_address, mailing_address_line2 } = this.data;

        info.push({
          label: this.t.mailingAddress,
          value: mailling_address ? mailling_address.trim() : null,
          extra: mailing_address_line2 ? mailing_address_line2.trim() : null,
          href: null,
          type: "address",
        });
      }

      // Website
      if (this.data.website) {
        let website = this.data.website.trim();
        if (!/^https?:\/\//i.test(website)) {
          website = "http://" + website;
        }
        info.push({
          label: this.t.visitWebsite,
          value: this.data.website,
          href: website,
          type: "website",
        });
      }

      // Social media
      if (Array.isArray(this.data.social_media)) {
        this.data.social_media.forEach((social) => {
          if (social?.url) {
            let url = social.url.trim();
            if (!/^https?:\/\//i.test(url)) {
              url = "http://" + url;
            }
            info.push({
              label: this.t.followMe,
              value: social.user_name || social.url,
              href: url,
              type: social.platform.toLowerCase(),
            });
          }
        });
      }

      return info;
    },
    saveContactButtonText() {
      return this.isSavingContact ? this.t.savingContact : this.t.saveContact;
    },
  },
  methods: {
    handleOpenLink(url) {
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    getIcon(type) {
      const icons = {
        phone: "PhoneIcon",
        direct_line: "MessageIcon",
        email: "MailIcon",
        website: "WorldIcon",
        facebook: "BrandFacebookIcon",
        instagram: "BrandInstagramIcon",
        tiktok: "BrandTiktokIcon",
        youtube: "BrandYoutubeIcon",
        address: "MapPinIcon",
        wsp: "BrandWhatsappIcon",
      };
      return icons[type] || "InfoCircleIcon";
    },
  },
};
</script>
<style lang="scss">
@import "@/assets/scss/helpers/helpers";

.bu-card-template {
  /* ===== base tokens ===== */
  --font-sans: ui-sans-serif, system-ui, sans-serif;
  --radius: 0.625rem;
  --radius-xl: calc(var(--radius) + 4px);
  --radius-2xl: 1rem;
  --shadow-lg: 0 0 0px #0000, 0 0 0px #0000, 0 0 0px #0000, 0 0 0px #0000,
    0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);

  --text-xs: #{px-to-rem(12)}; /* 12px */
  --text-xs--line-height: calc(1.5 / #{px-to-rem(12, false)});
  --text-sm: #{px-to-rem(14)}; /* 14px */
  --text-sm--line-height: calc(1.5 / #{px-to-rem(14, false)});
  --text-base: #{px-to-rem(16)}; /* 16px */
  --text-base--line-height: calc(1.5 / #{px-to-rem(16, false)});

  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;

  --container-sm: 26rem;

  /* ===== color palette ===== */
  --color-black: #000;
  --color-white: #fff;

  --color-blue-500: oklch(62.3% 0.214 259.815);
  --color-blue-600: oklch(54.6% 0.245 262.881);
  --color-blue-700: oklch(48.8% 0.243 264.376);
  --color-blue-800: oklch(42.4% 0.199 265.638);
  --color-blue-900: oklch(37.9% 0.146 265.522);

  --color-gray-50: oklch(98.5% 0.002 247.839);
  --color-gray-100: oklch(96.7% 0.003 264.542);
  --color-gray-200: oklch(92.8% 0.006 264.531);
  --color-gray-300: oklch(87.2% 0.01 258.338);
  --color-gray-400: oklch(70.7% 0.022 261.325);
  --color-gray-600: oklch(44.6% 0.03 256.802);
  --color-gray-700: oklch(37.3% 0.034 259.733);
  --color-gray-800: oklch(27.8% 0.033 256.848);
  --color-gray-900: oklch(21% 0.034 264.665);

  --color-green-50: oklch(98.2% 0.018 155.826);
  --color-green-100: oklch(96.2% 0.044 156.743);
  --color-green-200: oklch(92.5% 0.084 155.995);
  --color-green-400: oklch(79.2% 0.209 151.711);
  --color-green-500: oklch(72.3% 0.219 149.579);
  --color-green-600: oklch(62.7% 0.194 149.214);
  --color-green-700: oklch(52.7% 0.154 150.069);
  --color-green-800: oklch(44.8% 0.119 151.328);
  --color-green-900: oklch(39.3% 0.095 152.535);

  --color-purple-50: oklch(97.7% 0.014 308.299);
  --color-purple-100: oklch(94.6% 0.033 307.174);
  --color-purple-200: oklch(90.2% 0.063 306.703);
  --color-purple-400: oklch(71.4% 0.203 305.504);
  --color-purple-500: oklch(62.7% 0.265 303.9);
  --color-purple-600: oklch(55.8% 0.288 302.321);
  --color-purple-700: oklch(49.6% 0.265 301.924);
  --color-purple-800: oklch(43.8% 0.218 303.724);
  --color-purple-900: oklch(38.1% 0.176 304.987);

  --color-teal-50: oklch(98.4% 0.014 180.72);
  --color-teal-100: oklch(95.3% 0.051 180.801);
  --color-teal-200: oklch(91% 0.096 180.426);
  --color-teal-400: oklch(77.7% 0.152 181.912);
  --color-teal-500: oklch(70.4% 0.14 182.503);
  --color-teal-600: oklch(60% 0.118 184.704);
  --color-teal-700: oklch(51.1% 0.096 186.391);
  --color-teal-800: oklch(43.7% 0.078 188.216);
  --color-teal-900: oklch(38.6% 0.063 188.416);

  --color-orange-50: oklch(98% 0.016 73.684);
  --color-orange-100: oklch(95.4% 0.038 75.164);
  --color-orange-200: oklch(90.1% 0.076 70.697);
  --color-orange-300: oklch(83.7% 0.128 66.29);
  --color-orange-400: oklch(75% 0.183 55.934);
  --color-orange-500: oklch(70.5% 0.213 47.604);
  --color-orange-600: oklch(64.6% 0.222 41.116);
  --color-orange-700: oklch(55.3% 0.195 38.402);
  --color-orange-800: oklch(47% 0.157 37.304);
  --color-orange-900: oklch(40.8% 0.123 38.172);

  --color-amber-50: oklch(98.7% 0.022 95.277);
  --color-amber-100: oklch(96.2% 0.059 95.617);
  --color-amber-200: oklch(92.4% 0.12 95.746);
  --color-amber-500: oklch(76.9% 0.188 70.08);
  --color-amber-600: oklch(66.6% 0.179 58.318);
  --color-amber-700: oklch(55.5% 0.163 48.998);
  --color-amber-800: oklch(47.3% 0.137 46.201);
  --color-amber-900: oklch(41.4% 0.112 45.904);

  --color-red-500: oklch(63.7% 0.237 25.331);

  --color-yellow-500: oklch(79.5% 0.184 86.047);

  --color-pink-500: oklch(65.6% 0.241 354.308);

  --color-slate-600: oklch(44.6% 0.043 257.281);
  --color-slate-700: oklch(37.2% 0.044 257.287);

  /* ===== template custom variables ===== */
  /* Card Base */
  --bg-card: var(--color-white);
  --border-card: var(--color-gray-100);

  /* Profile */
  --bg-avatar: var(--color-blue-900);
  --text-avatar: var(--color-white);
  --text-name: var(--color-gray-900);
  --text-job-title: var(--color-gray-600);

  /* Save Contact Button */
  --bg-button: var(--color-blue-900);
  --bg-button-hover: var(--color-blue-800);
  --text-button: var(--color-white);

  /* Contact Info */
  --bg-info: transparent;
  --bg-info-hover: var(--color-gray-50);
  --text-info-label: var(--color-gray-900);
  --text-info-value: var(--color-gray-600);
  --border-info: var(--color-gray-200);

  --bg-icon-phone: var(--color-blue-500);
  --bg-icon-direct_line: var(--color-orange-500);
  --bg-icon-whatsapp: var(--color-green-600);
  --bg-icon-email: var(--color-blue-500);
  --bg-icon-address: var(--color-red-500);
  --bg-icon-website: var(--color-purple-600);
  --bg-icon-facebook: var(--color-blue-600);
  --bg-icon-instagram: var(--color-gray-900);
  --gradient-instagram: linear-gradient(
    to bottom right,
    var(--color-pink-500) 0%,
    var(--color-red-500) 50%,
    var(--color-yellow-500) 100%
  );
  --bg-icon-tiktok: var(--color-black);
  --bg-icon-youtube: var(--color-red-500);

  &.template-2,
  &.template-7,
  &.template-11,
  &.template-12 {
    /* Profile */
    --bg-avatar: var(--color-gray-900);

    /* Save Contact Button */
    --bg-button: var(--color-gray-900);
    --bg-button-hover: var(--color-gray-800);
  }
  &.template-3 {
    /* Card Base */
    --bg-card: color-mix(in oklab, var(--color-green-50) 50%, transparent);
    --border-card: color-mix(in oklab, var(--color-green-200) 70%, transparent);

    /* Profile */
    --bg-avatar: var(--color-green-700);
    --text-job-title: var(--color-green-700);

    /* Save Contact Button */
    --bg-button: var(--color-green-700);
    --bg-button-hover: var(--color-green-800);

    /* Contact Info */
    --bg-info-hover: color-mix(
      in oklab,
      var(--color-green-100) 50%,
      transparent
    );
    --border-info: var(--color-green-200);
  }
  &.template-4 {
    /* Card Base */
    --bg-card: color-mix(in oklab, var(--color-purple-50) 50%, transparent);
    --border-card: color-mix(
      in oklab,
      var(--color-purple-200) 70%,
      transparent
    );

    /* Profile */
    --bg-avatar: var(--color-purple-600);
    --text-job-title: var(--color-purple-600);

    /* Save Contact Button */
    --bg-button: var(--color-purple-600);
    --bg-button-hover: var(--color-purple-700);

    /* Contact Info */
    --bg-info-hover: color-mix(
      in oklab,
      var(--color-purple-100) 50%,
      transparent
    );
    --border-info: var(--color-purple-200);
  }
  &.template-5 {
    /* Card Base */
    --bg-card: color-mix(in oklab, var(--color-teal-50) 50%, transparent);
    --border-card: color-mix(in oklab, var(--color-teal-200) 70%, transparent);

    /* Profile */
    --bg-avatar: var(--color-teal-700);
    --text-job-title: var(--color-teal-700);

    /* Save Contact Button */
    --bg-button: var(--color-teal-700);
    --bg-button-hover: var(--color-teal-800);

    /* Contact Info */
    --bg-info-hover: color-mix(
      in oklab,
      var(--color-teal-100) 50%,
      transparent
    );
    --border-info: var(--color-teal-200);
  }
  &.template-6 {
    /* Card Base */
    --bg-card: color-mix(in oklab, var(--color-orange-50) 50%, transparent);
    --border-card: color-mix(
      in oklab,
      var(--color-orange-200) 70%,
      transparent
    );

    /* Profile */
    --bg-avatar: var(--color-orange-800);
    --text-name: var(--color-orange-900);
    --text-job-title: var(--color-orange-600);

    /* Save Contact Button */
    --bg-button: var(--color-orange-800);
    --bg-button-hover: var(--color-orange-900);

    /* Contact Info */
    --bg-info-hover: color-mix(
      in oklab,
      var(--color-orange-100) 50%,
      transparent
    );
    --border-info: var(--color-orange-200);
    --text-info-label: var(--color-orange-900);
    --text-info-value: var(--color-orange-600);
  }
  &.template-7 {
    /* Contact Info */
    --bg-icon-email: var(--color-orange-500);
    --bg-icon-website: var(--color-slate-700);
    --bg-icon-direct_line: var(--color-blue-600);
  }
  &.template-8 {
    /* Contact Info */
    --bg-icon-email: var(--color-red-500);
    --bg-icon-website: var(--color-blue-900);
    /*--bg-icon-facebook: var(--color-blue-900);
    --bg-icon-instagram: var(--color-orange-500);
    --gradient-instagram: unset;
    --bg-icon-tiktok: var(--color-blue-900);
    --bg-icon-youtube: var(--color-orange-500);*/
  }
  &.template-9 {
    /* Profile */
    --bg-avatar: var(--color-green-700);

    /* Save Contact Button */
    --bg-button: var(--color-green-700);
    --bg-button-hover: var(--color-green-800);

    /* Contact Info */
    --bg-icon-phone: var(--color-blue-500);
    --bg-icon-email: var(--color-orange-500);
    --bg-icon-website: var(--color-green-700);
    /*--bg-icon-facebook: var(--color-red-500);
    --gradient-instagram: linear-gradient(
      to bottom right,
      var(--color-green-700) 0%,
      var(--color-orange-600) 50%,
      var(--color-orange-600) 100%
    );
    --bg-icon-tiktok: var(--color-green-700);
    --bg-icon-youtube: var(--color-orange-600);*/
    --bg-icon-direct_line: var(--color-blue-600);
  }
  &.template-10 {
    /* Profile */
    --bg-avatar: var(--color-teal-700);

    /* Save Contact Button */
    --bg-button: var(--color-teal-700);
    --bg-button-hover: var(--color-teal-800);

    /* Contact Info */
    --bg-icon-email: var(--color-teal-600);
    --bg-icon-website: var(--color-orange-400);
  }
  &.template-11 {
    /* Card Base */
    --bg-card: color-mix(in oklab, var(--color-amber-50) 5%, transparent);
    --border-card: color-mix(in oklab, var(--color-amber-200) 50%, transparent);

    /* Profile */
    --bg-avatar: var(--color-amber-500);

    /* Contact Info */
    --bg-info-hover: color-mix(
      in oklab,
      var(--color-amber-100) 50%,
      transparent
    );
    --border-info: var(--color-amber-200);

    --bg-icon-email: var(--color-orange-500);
    --bg-icon-website: var(--color-slate-700);
    --bg-icon-direct_line: var(--color-blue-600);
  }
  &.template-12 {
    /* Card Base */
    --bg-card: color-mix(in oklab, var(--color-teal-50) 5%, transparent);
    --border-card: color-mix(in oklab, var(--color-teal-200) 50%, transparent);

    /* Profile */
    --bg-avatar: var(--color-teal-500);

    /* Contact Info */
    --bg-info-hover: color-mix(
      in oklab,
      var(--color-teal-100) 50%,
      transparent
    );
    --border-info: var(--color-teal-200);

    --bg-icon-phone: var(--color-teal-600);
    --bg-icon-direct_line: var(--color-slate-700);
    --bg-icon-email: var(--color-teal-600);
    --bg-icon-address: var(--color-slate-700);
    --bg-icon-website: var(--color-teal-700);
    /*--bg-icon-facebook: var(--color-slate-600);
    --gradient-instagram: linear-gradient(
      to bottom right,
      var(--color-teal-500) 0%,
      var(--color-teal-900) 100%
    );
    --bg-icon-youtube: var(--color-teal-900);*/
  }

  background-color: var(--bg-card);
  box-shadow: var(--shadow-lg);
  padding: 1.5rem;
  border-radius: var(--radius-2xl);
  width: 100%;
  max-width: var(--container-sm);
  margin: #{px-to-rem(24)} auto;
  border: 1px solid var(--border-card);
  font-family: var(--font-sans);
  @include space-y(1.5rem);

  .badge-ct-custom-primary {
    background-color: var(--bg-avatar);
    color: var(--text-avatar);
    font-weight: var(--font-weight-bold);
    .b-avatar-img {
      border: 2px solid transparent;
    }
  }
  .profile-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    .badge-ct-custom-primary {
      margin-bottom: 1rem;
    }
    .full-name {
      color: var(--text-name);
      font-weight: var(--font-weight-bold);
      font-size: var(--text-base);
      line-height: var(--text-base--line-height);
      text-align: center;
      word-break: break-word;
    }
    .job-title {
      margin-top: 0.25rem;
      color: var(--text-job-title);
      font-size: var(--text-sm);
      line-height: var(--text-sm--line-height);
      font-weight: var(--font-weight-medium);
      text-align: center;
      word-break: break-word;
    }
  }
  .logo-services {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    img {
      border: 2px solid var(--border-info);
      box-shadow: var(--shadow-lg);
    }
  }
  .business-hours {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border: 1px solid var(--border-info);
    border-radius: var(--radius);
    .label {
      font-size: var(--text-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--text-info-label);
    }
    .hours-list {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      .hours-item {
        display: flex;
        justify-content: space-between;
        gap: 0.25rem;
        .day {
          font-size: var(--text-sm);
          font-weight: var(--font-weight-medium);
          color: var(--text-info-label);
        }
        .time-container {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          .time {
            font-size: var(--text-sm);
            color: var(--text-info-value);
            white-space: nowrap;
          }
        }
      }
    }
  }
  .btn.btn-ct-custom-primary {
    width: 100%;
    background-color: var(--bg-button);
    color: var(--text-button);
    font-weight: var(--font-weight-semibold);
    border-radius: var(--radius-xl);
    font-size: var(--text-sm);
    &:hover {
      color: var(--text-button);
      background-color: var(--bg-button-hover);
    }
  }
  .contact-info {
    @include space-y(0.5rem);
    .info-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      background-color: var(--bg-info);
      border: 1px solid var(--border-info);
      border-radius: var(--radius);
      padding: 0.75rem;
      cursor: pointer;
      transition: all 0.3s ease;
      &:hover {
        background-color: var(--bg-info-hover);
      }
      .info-icon {
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 9999px;
        color: var(--color-white);
        &.phone {
          background-color: var(--bg-icon-phone);
        }
        &.email {
          background-color: var(--bg-icon-email);
        }
        &.website {
          background-color: var(--bg-icon-website);
        }
        &.facebook {
          background-color: var(--bg-icon-facebook);
        }
        &.instagram {
          background-color: var(--bg-icon-instagram);
          background-image: var(--gradient-instagram);
        }
        &.tiktok {
          background-color: var(--bg-icon-tiktok);
        }
        &.youtube {
          background-color: var(--bg-icon-youtube);
        }
        &.direct_line {
          background-color: var(--bg-icon-direct_line);
        }
        &.address {
          background-color: var(--bg-icon-address);
        }
        &.wsp {
          background-color: var(--bg-icon-whatsapp);
        }
      }
      .info {
        flex: 1;
        text-align: center;
        overflow: hidden;
        .label {
          color: var(--text-info-label);
          font-weight: var(--font-weight-medium);
          font-size: var(--text-sm);
          line-height: var(--text-sm--line-height);
        }
        .value {
          color: var(--text-info-value);
          font-size: var(--text-xs);
          line-height: var(--text-xs--line-height);
          &:not(.address) {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
          &.address {
            white-space: normal;
            word-break: break-word;
          }
        }
      }
      .custom-icon {
        flex: none;
      }
    }
  }
}

.dark-layout {
  .bu-card-template {
    /* Card Base */
    --bg-card: var(--color-gray-900);
    --border-card: var(--color-gray-700);

    /* Profile */
    --text-name: var(--color-white);
    --text-job-title: var(--color-gray-300);

    /* Contact Info */
    --bg-info: transparent;
    --bg-info-hover: var(--color-gray-800);
    --text-info-label: var(--color-white);
    --text-info-value: var(--color-gray-400);
    --border-info: var(--color-gray-600);

    &.template-2,
    &.template-7,
    &.template-11,
    &.template-12 {
      /* Profile */
      --bg-avatar: var(--color-gray-700);

      /* Save Contact Button */
      --bg-button: var(--color-gray-700);
      --bg-button-hover: var(--color-gray-600);
    }
    &.template-3 {
      /* Card Base */
      --bg-card: color-mix(in oklab, var(--color-green-900) 20%, transparent);
      --border-card: color-mix(
        in oklab,
        var(--color-green-700) 50%,
        transparent
      );

      /* Profile */
      --text-job-title: var(--color-green-400);

      /* Contact Info */
      --bg-info-hover: color-mix(
        in oklab,
        var(--color-green-800) 30%,
        transparent
      );
      --border-info: color-mix(
        in oklab,
        var(--color-green-600) 30%,
        transparent
      );
    }
    &.template-4 {
      /* Card Base */
      --bg-card: color-mix(in oklab, var(--color-purple-900) 20%, transparent);
      --border-card: color-mix(
        in oklab,
        var(--color-purple-700) 50%,
        transparent
      );

      /* Profile */
      --text-job-title: var(--color-purple-400);

      /* Contact Info */
      --bg-info-hover: color-mix(
        in oklab,
        var(--color-purple-800) 30%,
        transparent
      );
      --border-info: color-mix(
        in oklab,
        var(--color-purple-600) 30%,
        transparent
      );
    }
    &.template-5 {
      /* Card Base */
      --bg-card: color-mix(in oklab, var(--color-teal-900) 20%, transparent);
      --border-card: color-mix(
        in oklab,
        var(--color-teal-700) 50%,
        transparent
      );

      /* Profile */
      --text-job-title: var(--color-teal-400);

      /* Contact Info */
      --bg-info-hover: color-mix(
        in oklab,
        var(--color-teal-800) 30%,
        transparent
      );
      --border-info: color-mix(
        in oklab,
        var(--color-teal-600) 30%,
        transparent
      );
    }
    &.template-6 {
      /* Card Base */
      --bg-card: color-mix(in oklab, var(--color-orange-900) 20%, transparent);
      --border-card: color-mix(
        in oklab,
        var(--color-orange-700) 50%,
        transparent
      );

      /* Profile */
      --bg-avatar: var(--color-orange-700);
      --text-name: var(--color-orange-200);
      --text-job-title: var(--color-orange-300);

      /* Save Contact Button */
      --bg-button: var(--color-orange-700);
      --bg-button-hover: var(--color-orange-800);

      /* Contact Info */
      --bg-info-hover: color-mix(
        in oklab,
        var(--color-orange-800) 30%,
        transparent
      );
      --border-info: color-mix(
        in oklab,
        var(--color-orange-600) 30%,
        transparent
      );
      --text-info-label: var(--color-orange-200);
      --text-info-value: var(--color-orange-400);
    }
    &.template-7 {
      /* Contact Info */
      --bg-icon-email: var(--color-orange-500);
      --bg-icon-website: var(--color-slate-700);
    }
    &.template-11 {
      /* Card Base */
      --bg-card: color-mix(in oklab, var(--color-amber-900) 5%, transparent);
      --border-card: color-mix(
        in oklab,
        var(--color-amber-700) 20%,
        transparent
      );

      /* Profile */
      --bg-avatar: var(--color-amber-500);

      /* Contact Info */
      --bg-info-hover: color-mix(
        in oklab,
        var(--color-amber-800) 15%,
        transparent
      );
      --border-info: color-mix(
        in oklab,
        var(--color-amber-600) 30%,
        transparent
      );
    }
    &.template-12 {
      /* Card Base */
      --bg-card: color-mix(in oklab, var(--color-teal-900) 5%, transparent);
      --border-card: color-mix(
        in oklab,
        var(--color-teal-700) 20%,
        transparent
      );

      /* Profile */
      --bg-avatar: var(--color-teal-500);

      /* Contact Info */
      --bg-info-hover: color-mix(
        in oklab,
        var(--color-teal-800) 15%,
        transparent
      );
      --border-info: color-mix(
        in oklab,
        var(--color-teal-600) 30%,
        transparent
      );
    }
  }
}
</style>
